import { ReactElement, useMemo } from 'react';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { Button } from '../../../components/Button/Button';
import classes from './CashbackPage.module.css';
import { Alert } from '../../../components/Alert/Alert';
import { useGetPartnerCashbackSummaryQuery } from '../../../hooks/queries/useGetPartnerCashbackSummaryQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { StatsCardsGrid, type StatsCardConfig } from '../../../components/StatsCard/StatsCard';
import { WalletIcon, CalendarIcon } from '@navikt/aksel-icons';
import type { CustomerCashback, CashbackStatus, UpcomingChange } from '../../../api/queries';

const PAGE_TITLE = 'Cashback-oversikt';
const ERROR_MESSAGE = 'Kunne ikke laste cashback-data. Prøv igjen senere.';

const formatNOK = (amount: number): string => {
  return new Intl.NumberFormat('no-NO', {
    style: 'currency',
    currency: 'NOK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('no-NO');
};

const getStatusLabel = (status: CashbackStatus): string => {
  const labels: Record<CashbackStatus, string> = {
    active: 'Aktiv',
    expiring_soon: 'Utløper snart',
    not_started: 'Ikke startet',
    expired: 'Utløpt',
  };
  return labels[status];
};

const getStatusClass = (status: CashbackStatus): string => {
  const classMap: Record<CashbackStatus, string> = {
    active: classes.statusActive,
    expiring_soon: classes.statusExpiringSoon,
    not_started: classes.statusNotStarted,
    expired: classes.statusExpired,
  };
  return `${classes.statusBadge} ${classMap[status]}`;
};

const createStatsCards = (data: {
  totalMonthlyNOK: number;
  totalYearlyNOK: number;
  totalRemainingNOK: number;
  activeCustomerCount: number;
  expiringCustomerCount: number;
}): readonly StatsCardConfig[] => [
  {
    title: 'Månedlig cashback',
    icon: <WalletIcon />,
    value: formatNOK(data.totalMonthlyNOK),
    meta: 'per måned',
  },
  {
    title: 'Årlig cashback',
    icon: <WalletIcon />,
    value: formatNOK(data.totalYearlyNOK),
    meta: 'basert på faktiske avtaler',
  },
  {
    title: 'Total gjenstående',
    icon: <CalendarIcon />,
    value: formatNOK(data.totalRemainingNOK),
    meta: 'alle aktive avtaler',
  },
  {
    title: 'Aktive kunder',
    icon: <WalletIcon />,
    value: data.activeCustomerCount,
    meta: data.expiringCustomerCount > 0
      ? `${data.expiringCustomerCount} utløper snart`
      : undefined,
  },
];

const exportToCSV = (customers: readonly CustomerCashback[], partnerName: string) => {
  const headers = [
    'Kunde',
    'Status',
    'Månedlig cashback (NOK)',
    'Årlig cashback (NOK)',
    'Total gjenstående cashback (NOK)',
    'Måneder igjen',
    'Faktureringsperiode',
    'Start dato',
    'Sluttdato',
    'Pris per plass (øre)',
    'Antall plasser',
  ];

  const rows = customers.map((customer) => [
    customer.customerName,
    getStatusLabel(customer.status),
    customer.monthlyCashbackNOK.toString(),
    customer.yearlyCashbackNOK.toString(),
    customer.remainingCashbackNOK.toString(),
    customer.monthsRemaining?.toString() ?? 'Ubestemt',
    customer.billingPeriod ?? '-',
    formatDate(customer.billingStartDate),
    formatDate(customer.contractEndDate),
    customer.pricePerSeat?.toString() ?? '-',
    customer.availableSeats?.toString() ?? '-',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `cashback-${partnerName}-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CashbackTable = ({ customers }: { customers: readonly CustomerCashback[] }) => (
  <table className={classes.table}>
    <thead>
      <tr>
        <th>Kunde</th>
        <th>Status</th>
        <th className={classes.numberHeader}>Månedlig</th>
        <th className={classes.numberHeader}>Årlig</th>
        <th className={classes.numberHeader}>Total gjenst.</th>
        <th className={classes.numberHeader}>Mnd igjen</th>
        <th>Periode</th>
        <th>Start</th>
        <th>Slutt</th>
      </tr>
    </thead>
    <tbody>
      {customers.map((customer) => (
        <tr key={customer.customerId}>
          <td>{customer.customerName}</td>
          <td>
            <span className={getStatusClass(customer.status)}>
              {getStatusLabel(customer.status)}
            </span>
          </td>
          <td className={classes.numberCell}>{formatNOK(customer.monthlyCashbackNOK)}</td>
          <td className={classes.numberCell}>{formatNOK(customer.yearlyCashbackNOK)}</td>
          <td className={classes.numberCell}>{formatNOK(customer.remainingCashbackNOK)}</td>
          <td className={classes.numberCell}>
            {customer.monthsRemaining ?? 'Ubestemt'}
          </td>
          <td>{customer.billingPeriod ?? '-'}</td>
          <td>{formatDate(customer.billingStartDate)}</td>
          <td>{formatDate(customer.contractEndDate)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const UpcomingChangesTable = ({ changes }: { changes: readonly UpcomingChange[] }) => {
  if (changes.length === 0) {
    return <Paragraph>Ingen kommende endringer de neste 90 dagene.</Paragraph>;
  }

  return (
    <table className={classes.upcomingChangesTable}>
      <thead>
        <tr>
          <th>Dato</th>
          <th>Type</th>
          <th>Kunde</th>
          <th>Beskrivelse</th>
          <th className={classes.numberHeader}>Påvirkning (mnd)</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change, index) => (
          <tr key={index}>
            <td>{formatDate(change.date)}</td>
            <td>{change.type === 'contract_expiry' ? 'Avtale utløper' : 'Fakturering starter'}</td>
            <td>{change.customerName}</td>
            <td>{change.description}</td>
            <td className={`${classes.numberCell} ${change.impactMonthlyNOK < 0 ? classes.negativeImpact : classes.positiveImpact}`}>
              {change.impactMonthlyNOK < 0 ? '' : '+'}{formatNOK(change.impactMonthlyNOK)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const CashbackPage = (): ReactElement => {
  const { data, isLoading, error } = useGetPartnerCashbackSummaryQuery();

  const handleExport = () => {
    if (data) {
      exportToCSV(data.customers, data.partnerInfo.name);
    }
  };

  const statsCards = useMemo(() => {
    if (!data) return [];
    return createStatsCards(data.summary);
  }, [data]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <div>
            <Heading as="h1">{PAGE_TITLE}</Heading>
            {data?.partnerInfo && (
              <Paragraph size="lg">
                {data.partnerInfo.name} - {data.partnerInfo.cashbackPercentage
                  ? `${(data.partnerInfo.cashbackPercentage / 100).toFixed(1)}%`
                  : 'Ingen'} cashback
              </Paragraph>
            )}
          </div>
          {data && (
            <Button variant="secondary" size="medium" onClick={handleExport}>
              Eksporter til CSV
            </Button>
          )}
        </div>
      </header>

      <div className={classes.content}>
        {isLoading && <GhostLoader />}

        {error && (
          <Alert variant="danger" size="md">
            <Paragraph>{ERROR_MESSAGE}</Paragraph>
          </Alert>
        )}

        {data && !isLoading && (
          <>
            <StatsCardsGrid cards={statsCards} columns={4} />

            <ContainerCard>
              <ContainerCard.Title>Cashback per kunde</ContainerCard.Title>
              <ContainerCard.Body noPadding>
                <CashbackTable customers={data.customers} />
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Kommende endringer (neste 90 dager)</ContainerCard.Title>
              <ContainerCard.Body>
                <UpcomingChangesTable changes={data.upcomingChanges} />
              </ContainerCard.Body>
            </ContainerCard>
          </>
        )}
      </div>
    </div>
  );
};
